import * as logger from 'universal-logger';
import { fetchEntity, fetchEntityIfNeeded } from '@classpass/saga-tools';
import { call, put, select, takeEvery, all, spawn } from 'redux-saga/effects';
import { serialize as serializeCookie } from 'cookie';
import { UnpaidAccountBanner } from 'components/Banner/Banners/UnpaidAccountBanner';
import { memberStatus } from 'enums/memberStatus';
import { registerBanner } from 'features/Banners/BannerHost';
import { addAnnouncement } from 'features/Banners/Banners.reducer';
import { watchBannerSagas } from 'features/Banners/Banners.saga';
import { reservationStatusChanged } from 'features/Reservation/Reservation.saga';
import {
  ANALYTICS_EC_ENABLED,
  ANALYTICS_GA_ENABLED,
  ANALYTICS_GTM_ENABLED,
  ANALYTICS_SEGMENT_ENABLED,
} from 'constants/analytics';
import { setUserSelectedLocation } from 'redux/modules/locale';
import {
  dashFeatureById,
  friendbuy,
  userCreditBalance,
  userPermissions,
  userProfile,
  userUpcomingReservations,
} from 'redux/saga/entities/bound';
import { readLocalStorage, removeLocalStorage, writeLocalStorage } from 'redux/saga/helpers/local-storage';
import { isLoggedIn } from 'redux/saga/selectors';
import { friendbuyCode, FBUY_COOKIE_NAME } from 'redux/saga/selectors/friendbuy';
import { geodetectMsa } from 'redux/saga/helpers';
import { getNextCycleDate } from 'redux/saga/helpers/credit-cycle';

export const HANDLE_GLOBAL_ERROR = 'APP/HANDLE_GLOBAL_ERROR';
export const registerGlobalError = error => ({ type: HANDLE_GLOBAL_ERROR, error });

export function* maybeShowUnpaidAccountAnnouncement(user) {
  if (memberStatus('UNPAID', user.membership.status)) {
    yield call(registerBanner, 'UnpaidAccountAnnouncement', UnpaidAccountBanner);
    yield put(addAnnouncement('unpaid_account', 'UnpaidAccountAnnouncement'));
  }
}

export function* addUserMsaAliasToLocalStorage(client) {
  try {
    const msa = yield call(geodetectMsa, client);
    if (msa) {
      yield call(writeLocalStorage, 'userMsaAlias', msa.alias);
    }
  } catch (err) {
    /* noop */
  }
}

export function* initializeReferAFriend(client) {
  const referAFriendCode = yield select(friendbuyCode);

  if (referAFriendCode) {
    try {
      yield call(fetchEntityIfNeeded, friendbuy, [client, referAFriendCode]);
      if (__CLIENT__ || __TEST__) {
        document.cookie = serializeCookie(FBUY_COOKIE_NAME, referAFriendCode);
      }
    } catch (err) {
      /* noop */
    }
  }
}

export function* initializeUserLocation() {
  try {
    const { lat, lon, expires } = yield call(readLocalStorage, 'userLocation', {});

    if (expires <= Date.now()) {
      yield call(removeLocalStorage, 'userLocation');
    } else if (lat && lon) {
      yield put(setUserSelectedLocation(lat, lon));
    }
  } catch (err) {
    /* noop */
  }
}

export function* doLoadCreditBalance(client, userId) {
  try {
    const nextSubscriptionCycleDate = yield call(getNextCycleDate, client, userId);
    const dataCalls = [call(fetchEntity, userCreditBalance, [client, userId])];

    if (nextSubscriptionCycleDate) {
      dataCalls.push(
        call(fetchEntity, userCreditBalance, [client, userId, { applicable_on: nextSubscriptionCycleDate }, true])
      );
    }

    yield all(dataCalls);
  } catch (error) {
    yield call(logger.error, 'Could not load credit balance for user', error);
  }
}

export function* handleReservationStatusChanged(client) {
  const isUserLoggedIn = yield select(isLoggedIn);

  if (!isUserLoggedIn) {
    return;
  }

  const user = yield call(fetchEntityIfNeeded, userProfile, [client]);

  yield all([
    call(doLoadCreditBalance, client, user.id),
    call(fetchEntity, userProfile, [client]),
    call(fetchEntity, userUpcomingReservations, [client, user.id]),
  ]);
}

export function* doHandleGlobalError(client, { error }) {
  const errorName = error.name || 'Critical Global Error';
  yield call(logger.critical, errorName, error);
}

export function* doLoadInitialApp(client) {
  const isUserLoggedIn = yield select(isLoggedIn);

  yield all([
    spawn(watchBannerSagas),
    call(initializeReferAFriend, client),
    call(initializeUserLocation),
    call(addUserMsaAliasToLocalStorage, client),
    call(fetchEntityIfNeeded, dashFeatureById, [client, ANALYTICS_SEGMENT_ENABLED]),
    call(fetchEntityIfNeeded, dashFeatureById, [client, ANALYTICS_GTM_ENABLED]),
    call(fetchEntityIfNeeded, dashFeatureById, [client, ANALYTICS_GA_ENABLED]),
    call(fetchEntityIfNeeded, dashFeatureById, [client, ANALYTICS_EC_ENABLED]),
  ]);

  if (!isUserLoggedIn) {
    return;
  }

  const user = yield call(fetchEntityIfNeeded, userProfile, [client]);

  yield all([
    call(doLoadCreditBalance, client, user.id),
    call(fetchEntityIfNeeded, userPermissions, [client, user.id]),
    call(fetchEntityIfNeeded, userUpcomingReservations, [client, user.id]),
    call(maybeShowUnpaidAccountAnnouncement, user),
  ]);
}

export function* watchErrorHandler(client) {
  yield takeEvery(HANDLE_GLOBAL_ERROR, doHandleGlobalError, client);
}

export function* watchReservationStatusChanged(client) {
  yield takeEvery(reservationStatusChanged().type, handleReservationStatusChanged, client);
}
